import React, { useCallback, useEffect, useState } from 'react';

import { Container } from '@/src/ui';
import { apiRequest } from '@/src/services';
import ExamHistoryFilters from './components/ExamHistoryFilters';
import { type ExamHistoryFilterState } from './models/filterDTOs';
import type { ExamHistoryResponse } from './models/examHistoryDTOs';
import ExamHistoryTable from './components/ExamHistoryTable';
import { useAuth } from '@/src/app/context/useAuth';

const viewModeIdToString: Record<number, string> = {
    1: 'exam',
    2: 'student',
};

const ExamHistoryPage: React.FC = () => {
    document.title = 'Exam History Report';

    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState<any[]>([]);
    const [hasSearched, setHasSearched] = useState(false);

    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const UNIVERSAL_FILTER_KEY = `universal-filters-${userId}`;

    const [filterState, setFilterState] = useState<ExamHistoryFilterState>(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    districtId: parsed.districtId || 0,
                    schoolId: parsed.schoolId || 0,
                    gradeLevelMasterId: parsed.gradeLevelMasterId || 0,
                    classId: parsed.classId || 0,
                    viewMode: 1,
                };
            }
        } catch {
            // intentionally empty
        }
        return {
            districtId: 0,
            schoolId: 0,
            gradeLevelMasterId: 0,
            classId: 0,
            viewMode: 1,
        };
    });

    useEffect(() => {
        if (!userId) return;
        const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
        const existingFilters = saved ? JSON.parse(saved) : {};
        const universalFilters = {
            ...existingFilters,
            gradeLevelMasterId: filterState.gradeLevelMasterId,
            districtId: filterState.districtId,
            schoolId: filterState.schoolId,
            classId: filterState.classId,
        };
        try {
            localStorage.setItem(UNIVERSAL_FILTER_KEY, JSON.stringify(universalFilters));
        } catch {
            // intentionally empty
        }
    }, [filterState, userId, UNIVERSAL_FILTER_KEY]);

    const refreshTableData = useCallback(
        async (filters: ExamHistoryFilterState = filterState) => {
            setError('');
            setLoaded(false);
            setHasSearched(true);
            const requestUrl =
                `reports/examHistory?districtId=${filters.districtId}&schoolId=${filters.schoolId}&gradeId=${filters.gradeLevelMasterId}` +
                (filters.classId ? `&classId=${filters.classId}` : '') +
                `&viewMode=${viewModeIdToString[filters.viewMode]}`;
            try {
                const res = await apiRequest<ExamHistoryResponse>('GET', requestUrl);
                setData(res.data.data);
            } catch {
                setError('Failed to load Exam History Report');
            } finally {
                setLoaded(true);
            }
        },
        [filterState]
    );

    return (
        <Container>
            <h1 className="page-header">Exam History Report</h1>
            <br />
            <ExamHistoryFilters
                filterState={filterState}
                setFilterState={setFilterState}
                refreshTableData={refreshTableData}
            />
            <ExamHistoryTable
                data={data}
                viewMode={viewModeIdToString[filterState.viewMode]}
                isLoading={hasSearched && !loaded}
                error={error}
            />
        </Container>
    );
};

export default ExamHistoryPage;
